import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import { parse, eachMonthOfInterval } from 'date-fns';
import { formatBackendDate } from '../../Helpers/utils';

/**
 * MonthlyValuesModal - Modal dialog for editing monthly values of a GL Key
 * 
 * @param {boolean} open - Whether modal is open
 * @param {object} glKey - The GL Key being edited
 * @param {object} originalValues - Original monthly values for revert functionality
 * @param {object} projectDateRange - { start: date, end: date }
 * @param {function} onValueChange - Callback when value changes (month, value)
 * @param {function} onRevertMonth - Callback to revert a single month
 * @param {function} onRevertAll - Callback to revert all months
 * @param {function} onClose - Callback to close modal
 */
const MonthlyValuesModal = ({ open, glKey, originalValues, projectDateRange, onValueChange, onRevertMonth, onRevertAll, onClose }) => {
  const [changedMonths, setChangedMonths] = useState(new Set());
  const [validation, setValidation] = useState({});
  const [localValues, setLocalValues] = useState({}); // Track values during editing

  // Initialize local values when glKey changes
  useEffect(() => {
    if (glKey && glKey.dates) {
      const formattedValues = {};
      Object.keys(glKey.dates).forEach(dateKey => {
        const value = glKey.dates[dateKey];
        formattedValues[dateKey] = value !== null && value !== undefined ? Math.round(value) : '';
      });
      setLocalValues(formattedValues);
    }
  }, [glKey]);

  // Track which months have been changed from original
  useEffect(() => {
    if (localValues && originalValues) {
      const changed = new Set();
      Object.keys(localValues).forEach(dateKey => {
        const currentValue = localValues[dateKey];
        const originalValue = originalValues[dateKey];
        // Compare values — treat null, undefined, and '' all as "blank"
        const currentStr = (currentValue !== null && currentValue !== undefined && currentValue !== '') ? String(Math.round(currentValue)).trim() : '';
        const originalStr = (originalValue !== null && originalValue !== undefined) ? String(Math.round(originalValue)).trim() : '';
        if (currentStr !== originalStr) {
          changed.add(dateKey);
        }
      });
      setChangedMonths(changed);
    }
  }, [localValues, originalValues]);

  /**
   * Generate month fields from project date range
   * @returns {Array} Array of month objects { key: 'YYYY-MM-DD', label: 'Mon YYYY' }
   */
  const generateMonthFields = () => {
    if (!projectDateRange || !projectDateRange.start || !projectDateRange.end) {
      return [];
    }

    try {
      // Parse dates using the same format as backend (yyyy-MM-dd)
      const startDate = parse(projectDateRange.start, 'yyyy-MM-dd', new Date());
      const endDate = parse(projectDateRange.end, 'yyyy-MM-dd', new Date());

      const months = eachMonthOfInterval({ start: startDate, end: endDate });

      return months.map(date => {
        // Backend stores dates as YYYY-MM-DD (first day of month)
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const key = `${year}-${month}-01`;
        
        // Use formatBackendDate to get the proper label (handles backend date offset)
        const label = formatBackendDate(key);
        
        return { key, label };
      });
    } catch (error) {
      console.error('Error generating month fields:', error);
      return [];
    }
  };

  /**
   * Handle revert single month value
   * @param {string} month - Month key to revert
   */
  const handleRevertMonth = (month) => {
    // Update local value to original — store null for blank originals to match storage format
    if (originalValues) {
      const orig = originalValues[month];
      setLocalValues(prev => ({
        ...prev,
        [month]: orig !== null && orig !== undefined ? Math.round(orig) : null,
      }));
    }

    // Call parent handler
    if (onRevertMonth) {
      onRevertMonth(month);
    }

    // Clear validation error if any
    setValidation(prev => {
      const newValidation = { ...prev };
      delete newValidation[month];
      return newValidation;
    });
  };

  /**
   * Handle revert all changes
   */
  const handleRevertAll = () => {
    // Update all local values to original
    if (originalValues) {
      const formattedValues = {};
      Object.keys(originalValues).forEach(dateKey => {
        const value = originalValues[dateKey];
        formattedValues[dateKey] = value !== null && value !== undefined ? Math.round(value) : '';
      });
      setLocalValues(formattedValues);
    }

    // Call parent handler
    if (onRevertAll) {
      onRevertAll();
    }
    
    // Clear all validation errors
    setValidation({});
  };

  /**
   * Handle month value change
   * @param {string} month - Month key (YYYY-MM-DD)
   * @param {string} value - New value
   */
  const handleMonthChange = (month, value) => {
    // Validate numeric input (allow negative numbers and decimals during typing)
    if (value !== '' && !/^-?\d*\.?\d*$/.test(value)) {
      setValidation(prev => ({
        ...prev,
        [month]: 'Value must be numeric',
      }));
      return;
    }

    // Clear validation error
    setValidation(prev => {
      const newValidation = { ...prev };
      delete newValidation[month];
      return newValidation;
    });

    // Update local state immediately for responsive UI
    setLocalValues(prev => ({
      ...prev,
      [month]: value,
    }));

    // Notify parent component - round to nearest whole number
    if (onValueChange) {
      const numericValue = value === '' ? null : Math.round(parseFloat(value));
      onValueChange(month, numericValue);
    }
  };

  /**
   * Get cell style for month field
   * @param {string} month - Month key
   * @returns {object} Style object
   */
  const getCellStyle = (month) => {
    if (validation[month]) {
      return { backgroundColor: '#ffebee' }; // Red for errors
    }
    if (changedMonths.has(month)) {
      return { backgroundColor: '#fff3cd' }; // Yellow for changes
    }
    return {};
  };

  const months = generateMonthFields();

  if (!glKey) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        Edit Monthly Values: {glKey.gl_key} - {glKey.account_name}
      </DialogTitle>
      
      <DialogContent dividers>
        {/* Monthly value fields in grid */}
        <Grid container spacing={2}>
          {months.map(({ key, label }) => {
            const isChanged = changedMonths.has(key);
            return (
              <Grid item xs={12} sm={6} md={3} key={key}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                  <IconButton
                    size="small"
                    onClick={() => handleRevertMonth(key)}
                    disabled={!isChanged}
                    title={isChanged ? "Revert to original value" : "No changes to revert"}
                    sx={{ 
                      mt: 0.5,
                      opacity: isChanged ? 1 : 0.3,
                      '&:disabled': {
                        cursor: 'not-allowed',
                      }
                    }}
                  >
                    <UndoIcon fontSize="small" />
                  </IconButton>
                  <TextField
                    label={label}
                    type="text"
                    size="small"
                    fullWidth
                    value={localValues[key] ?? ''}
                    onChange={(e) => handleMonthChange(key, e.target.value)}
                    error={!!validation[key]}
                    helperText={validation[key]}
                    sx={{
                      '& .MuiInputBase-root': getCellStyle(key),
                    }}
                  />
                </Box>
              </Grid>
            );
          })}
        </Grid>

        {/* Info text */}
        {changedMonths.size > 0 && (
          <Typography variant="caption" sx={{ mt: 2, display: 'block', color: '#856404' }}>
            Changed values are highlighted in yellow. Click "Save All" in the main page to commit all changes.
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={handleRevertAll}
          disabled={changedMonths.size === 0}
          startIcon={<UndoIcon />}
        >
          Revert All
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MonthlyValuesModal;
